import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Form, Spinner, Badge } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPencilAlt, faLayerGroup, faTag, faCubes, faInfoCircle, faFileInvoiceDollar } from '@fortawesome/free-solid-svg-icons';
import styled from 'styled-components';
import { fetchPricingPolicy, fetchPricingPolicyVersions } from 'hooks/CallApi';
import { formatDate } from 'components/common/SunnyUtils';

// --- Styled Components ---
const PageContainer = styled.div`
  padding: 2rem 1rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #64748b;
  font-weight: 600;
  padding: 0;
  background: transparent;
  border: none;
  
  &:hover, &:focus, &:active {
    color: #3b82f6;
    background: transparent !important;
    box-shadow: none !important;
    transform: translateX(-4px);
  }
  transition: all 0.2s ease;
`;

const StyledCard = styled(Card)`
  border: none;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  margin-bottom: 2rem;
`;

const CardHeader = styled.div`
  padding: 1.5rem 2rem;
  background: linear-gradient(to right, #f8fafc, #ffffff);
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PolicyTitle = styled.h3`
  margin: 0;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ActionButton = styled(Button)`
  border-radius: 8px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0.5rem 1rem;
`;

const InfoSection = styled.div`
  padding: 2rem;
  background-color: #ffffff;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const InfoCard = styled.div`
  background: #f8fafc;
  padding: 1.25rem;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  
  .label {
    color: #64748b;
    font-size: 0.85rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  
  .value {
    color: #0f172a;
    font-weight: 700;
    font-size: 1.1rem;
  }
`;

const VersionSelector = styled(Form.Select)`
  background-color: #f1f5f9;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-weight: 600;
  color: #3b82f6;
  cursor: pointer;
  width: auto;
  min-width: 120px;
  
  &:focus {
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
    border-color: #3b82f6;
  }
`;

const SectionTitle = styled.h5`
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 1.5rem 0;
  display: flex;
  align-items: center;
  gap: 10px;
  
  &::before {
    content: '';
    display: block;
    width: 4px;
    height: 20px;
    background-color: #3b82f6;
    border-radius: 4px;
  }
`;

const ServiceGroupCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
`;

const GroupHeader = styled.div`
  background: #f8fafc;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h6 {
    margin: 0;
    font-weight: 700;
    color: #334155;
    font-size: 1.1rem;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const ServiceItemRow = styled.div`
  padding: 1.25rem 1.5rem;
  border-bottom: 1px dashed #e2e8f0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const ItemName = styled.div`
  font-weight: 600;
  color: #0f172a;
  font-size: 1.05rem;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PriceTag = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  
  .amount {
    font-size: 1.25rem;
    font-weight: 700;
    color: #0d6efd;
  }
  
  .unit {
    font-size: 0.85rem;
    color: #64748b;
    font-weight: 500;
  }
`;

const TierTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-top: 1rem;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  
  th {
    background-color: #f1f5f9;
    color: #475569;
    font-weight: 600;
    font-size: 0.85rem;
    padding: 0.75rem 1rem;
    text-align: left;
    border-bottom: 1px solid #e2e8f0;
  }
  
  td {
    padding: 0.75rem 1rem;
    color: #334155;
    font-size: 0.9rem;
    border-bottom: 1px solid #f1f5f9;
  }
  
  tr:last-child td {
    border-bottom: none;
  }
  
  tr:hover td {
    background-color: #f8fafc;
  }
`;

const CompensationBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border: 1px solid #fef08a;
  border-radius: 8px;
  background-color: #fefce8;
  font-size: 0.9rem;
  box-shadow: 0 1px 2px rgba(234, 179, 8, 0.1);
  
  .label {
    color: #ca8a04;
    font-weight: 600;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .value {
    color: #854d0e;
    font-weight: 700;
  }
  
  &.compact {
    padding: 4px 10px;
    font-size: 0.85rem;
  }
`;

const TotalCompensationCard = styled.div`
  background: linear-gradient(to right, #eff6ff, #dbeafe);
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
  
  .title {
    color: #1e40af;
    font-weight: 700;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 1.05rem;
  }
  
  .amount {
    font-size: 1.25rem;
    font-weight: 800;
    color: #1d4ed8;
    margin: 0;
  }
`;

const VersionAlert = styled.div`
  background-color: #fffbeb;
  border: 1px solid #fde047;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #854d0e;
  font-weight: 500;
  margin-bottom: 1.5rem;
  
  .icon {
    color: #eab308;
    font-size: 1.25rem;
  }
`;

const PricingPolicyDetail = () => {
  const { pricingPolicyUuid } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [policyDetails, setPolicyDetails] = useState(null);
  
  const [versionData, setVersionData] = useState([]);
  const [selectedVersionUuid, setSelectedVersionUuid] = useState('');
  const [originalPolicyUuid, setOriginalPolicyUuid] = useState('');
  const [isLoadingVersion, setIsLoadingVersion] = useState(false);
  const [isLatestVersion, setIsLatestVersion] = useState(true);

  const fetchPolicyData = async (policyUuid) => {
    try {
      setIsLoading(true);
      const response = await fetchPricingPolicy(policyUuid);
      
      if (response && response.data) {
        setPolicyDetails(response.data);
        
        if (!originalPolicyUuid) {
          const originalUuid = response.data.originalPricingPolicyUuid || policyUuid;
          setOriginalPolicyUuid(originalUuid);
          fetchVersionsData(originalUuid);
        }
      } else {
        throw new Error('Invalid response data format');
      }
    } catch (error) {
      console.error('Error fetching policy data:', error);
      alert('요금정책 데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchVersionsData = async (originalUuid) => {
    try {
      const response = await fetchPricingPolicyVersions(originalUuid);
      
      if (response && response.data) {
        const versions = response.data;
        setVersionData(versions);
        
        const sortedVersions = [...versions].sort((a, b) => b.version - a.version);
        
        if (sortedVersions.length > 0) {
          const latestVersion = sortedVersions[0];
          setIsLatestVersion(pricingPolicyUuid === latestVersion.pricingPolicyUuid);
        }
        
        setSelectedVersionUuid(pricingPolicyUuid);
      }
    } catch (error) {
      console.error('Error fetching version data:', error);
    }
  };

  useEffect(() => {
    fetchPolicyData(pricingPolicyUuid);
  }, [pricingPolicyUuid]);

  const handleVersionChange = async (e) => {
    const newVersionUuid = e.target.value;
    setSelectedVersionUuid(newVersionUuid);
    setIsLoadingVersion(true);
    
    try {
      await fetchPolicyData(newVersionUuid);
      
      const sortedVersions = [...versionData].sort((a, b) => b.version - a.version);
      if (sortedVersions.length > 0) {
        const latestVersion = sortedVersions[0];
        setIsLatestVersion(newVersionUuid === latestVersion.pricingPolicyUuid);
      }
    } catch (error) {
      console.error('Error loading version data:', error);
    } finally {
      setIsLoadingVersion(false);
    }
  };

  const handleBackClick = () => navigate('/safedb/SA/pricingPolicy');

  const handleEditClick = () => {
    const editUuid = originalPolicyUuid || pricingPolicyUuid;
    navigate(`/safedb/SA/pricingPolicy/edit/${pricingPolicyUuid}?originalPricingPolicyUuid=${editUuid}`);
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(amount);
  };

  const getUnitLabel = (unit) => {
    switch (unit) {
      case 'count': return '개당';
      case 'session': return '회당';
      case 'month': return '월별';
      case 'year': return '연별';
      default: return unit || '-';
    }
  };

  const calculateTotalCompensation = () => {
    if (!policyDetails) return 0;
    let total = parseFloat(policyDetails.compensationAmount) || 0;
    if (policyDetails.serviceGroupList) {
      policyDetails.serviceGroupList.forEach(group => {
        total += parseFloat(group.compensationAmount) || 0;
        if (group.servicePriceList) {
          group.servicePriceList.forEach(price => {
            total += parseFloat(price.compensationAmount) || 0;
          });
        }
      });
    }
    return total;
  };

  const hasAnyCompensation = () => {
    if (!policyDetails) return false;
    if (parseFloat(policyDetails.compensationAmount) > 0) return true;
    if (policyDetails.serviceGroupList) {
      return policyDetails.serviceGroupList.some(group => {
        if (parseFloat(group.compensationAmount) > 0) return true;
        return group.servicePriceList?.some(price => parseFloat(price.compensationAmount) > 0);
      });
    }
    return false;
  };

  if (isLoading) {
    return (
      <PageContainer>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="text-center">
            <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
            <p className="mt-3 text-500 fw-semi-bold">요금정책 상세 정보를 불러오는 중입니다...</p>
          </div>
        </div>
      </PageContainer>
    );
  }
  
  return (
    <PageContainer>
      <HeaderSection>
        <BackButton onClick={handleBackClick}>
          <FontAwesomeIcon icon={faArrowLeft} />
          목록으로 돌아가기
        </BackButton>
      </HeaderSection>

      {!isLatestVersion && (
        <VersionAlert>
          <FontAwesomeIcon icon={faInfoCircle} className="icon" />
          <div>
            <strong>과거 버전 ({versionData.find(v => v.pricingPolicyUuid === selectedVersionUuid)?.version || ''})</strong>을 보고 계십니다. 
            수정은 최신 버전({versionData.sort((a, b) => b.version - a.version)[0]?.version || ''})에서만 가능합니다.
          </div>
        </VersionAlert>
      )}

      <StyledCard>
        <CardHeader>
          <PolicyTitle>
            <FontAwesomeIcon icon={faTag} className="text-primary" />
            {policyDetails?.pricingPolicyName || '-'}
          </PolicyTitle>
          <div className="d-flex gap-2">
            <div className="d-flex align-items-center gap-2 me-3">
              <span className="text-500 fw-bold text-sm">버전 선택:</span>
              <VersionSelector
                value={selectedVersionUuid}
                onChange={handleVersionChange}
                disabled={isLoadingVersion || versionData.length === 0} 
              >
                {versionData.map((version) => (
                  <option key={version.pricingPolicyUuid} value={version.pricingPolicyUuid}>
                    {version.version}
                  </option>
                ))}
              </VersionSelector>
              {isLoadingVersion && <Spinner animation="border" size="sm" variant="primary" />}
            </div>
            
            {isLatestVersion && (
              <ActionButton variant="primary" onClick={handleEditClick}>
                <FontAwesomeIcon icon={faPencilAlt} />
                정책 수정
              </ActionButton>
            )}
          </div>
        </CardHeader>
        
        <InfoSection>
          <InfoGrid>
            <InfoCard>
              <div className="label">설명</div>
              <div className="value text-500 fw-normal" style={{ fontSize: '1rem' }}>
                {policyDetails?.description || '설명 없음'}
              </div>
            </InfoCard>
            <InfoCard>
              <div className="label">등록 일시</div>
              <div className="value">
                {policyDetails?.insertTimeUtc ? formatDate(policyDetails.insertTimeUtc) : '-'}
              </div>
            </InfoCard>
            {parseFloat(policyDetails?.compensationAmount) > 0 && (
              <InfoCard style={{ background: '#fefce8', borderColor: '#fef08a' }}>
                <div className="label text-warning">
                  <FontAwesomeIcon icon={faTag} />
                  정책 레벨 보전금액
                </div>
                <div className="value text-warning-dark">
                  {formatCurrency(policyDetails.compensationAmount)}
                </div>
              </InfoCard>
            )}
          </InfoGrid>

          <SectionTitle className="mt-5">
            <FontAwesomeIcon icon={faCubes} className="text-primary" />
            서비스 구성 상세
          </SectionTitle>

          {isLoadingVersion ? (
            <div className="text-center py-5 bg-light rounded-3">
              <Spinner animation="grow" variant="primary" />
              <p className="mt-3 text-500 fw-bold">버전 정보 로딩 중...</p>
            </div>
          ) : policyDetails?.serviceGroupList?.length > 0 ? (
            policyDetails.serviceGroupList.map((group, index) => (
              <ServiceGroupCard key={index}>
                <GroupHeader>
                  <h6>
                    <FontAwesomeIcon icon={faLayerGroup} className="text-secondary opacity-50" />
                    {group.serviceItemName}
                  </h6>
                  {parseFloat(group.compensationAmount) > 0 && (
                    <CompensationBadge className="compact">
                      <span className="label">그룹 보전금액</span>
                      <span className="value">{formatCurrency(group.compensationAmount)}</span>
                    </CompensationBadge>
                  )}
                </GroupHeader>
                
                <div>
                  {group.servicePriceList && group.servicePriceList.length > 0 ? (
                    group.servicePriceList.map((price, pIndex) => (
                      <ServiceItemRow key={pIndex}>
                        <ItemHeader>
                          <div>
                            <ItemName>
                              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#3b82f6' }}></div>
                              {price.serviceItemName}
                            </ItemName>
                            {parseFloat(price.compensationAmount) > 0 && (
                              <div className="mt-2">
                                <CompensationBadge className="compact">
                                  <span className="label">항목 보전금액</span>
                                  <span className="value">{formatCurrency(price.compensationAmount)}</span>
                                </CompensationBadge>
                              </div>
                            )}
                          </div>
                          <PriceTag>
                            <span className="amount">{formatCurrency(price.price)}</span>
                            <span className="unit">{getUnitLabel(price.paymentUnit)}</span>
                          </PriceTag>
                        </ItemHeader>

                        {price.serviceTierPriceList && price.serviceTierPriceList.length > 0 && (
                          <TierTable>
                            <thead>
                              <tr>
                                <th>구간</th>
                                <th>할인 적용</th>
                                <th>최종 단가</th>
                              </tr>
                            </thead>
                            <tbody>
                              {price.serviceTierPriceList.map((tier, tIndex) => {
                                let discountedPrice = price.price;
                                if (tier.discountUnit === 'percent') {
                                  discountedPrice = price.price * (1 - tier.discountAmount / 100);
                                } else if (tier.discountUnit === 'won') {
                                  discountedPrice = price.price - tier.discountAmount;
                                }
                                
                                return (
                                  <tr key={tIndex}>
                                    <td>
                                      <strong>{tier.startAmount}</strong> ~ {tier.endAmount >= 99999 ? '무제한' : <strong>{tier.endAmount}</strong>}
                                    </td>
                                    <td>
                                      <Badge bg={tier.discountUnit === 'percent' ? 'info' : 'success'} className="me-2">
                                        {tier.discountAmount}{tier.discountUnit === 'percent' ? '%' : '원'} 할인
                                      </Badge>
                                    </td>
                                    <td className="fw-bold text-primary">
                                      {formatCurrency(discountedPrice)} <span className="text-400 fw-normal fs--1">/{getUnitLabel(price.paymentUnit)}</span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </TierTable>
                        )}
                      </ServiceItemRow>
                    ))
                  ) : (
                    <div className="p-4 text-center text-400">등록된 서비스 항목이 없습니다.</div>
                  )}
                </div>
              </ServiceGroupCard>
            ))
          ) : (
            <div className="text-center py-5 bg-light rounded-3 border border-dashed">
              <p className="text-500 mb-0">구성된 서비스가 없습니다.</p>
            </div>
          )}

          {hasAnyCompensation() && (
            <TotalCompensationCard>
              <div className="title">
                <FontAwesomeIcon icon={faFileInvoiceDollar} size="lg" />
                기본 청구 금액 (보전금액 총합)
              </div>
              <div className="amount">
                {formatCurrency(calculateTotalCompensation())}
              </div>
            </TotalCompensationCard>
          )}

        </InfoSection>
      </StyledCard>
    </PageContainer>
  );
};

export default PricingPolicyDetail;
